import { cn, cva } from 'noddi-ui-common';

import { webButtonStyling } from '../helpers/webButtonStyling';

// `segmented` is a switch inside content (NoddiSegmentedControl, tabs within a card). `underline` is
// page navigation: tabs sitting on the bottom border of a page header (NavioPage).
export const selectionControlListVariants = cva('flex h-auto w-fit flex-wrap', {
  variants: {
    variant: {
      segmented: 'items-center justify-evenly rounded-lg bg-gray-200 p-[3px]',
      underline: 'items-end gap-0.5'
    }
  },
  defaultVariants: {
    variant: 'segmented'
  }
});

export const selectionControlItemVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap focus-visible:border-ring focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    webButtonStyling,
    // After `webButtonStyling` on purpose: it carries `focus-visible:outline-hidden`, which
    // sets `--tw-outline-style: none` and would leave `outline-1` resolving to no outline.
    // `outline-solid` shares tailwind-merge's outline-style group, so `outline-hidden` is
    // dropped outright instead of depending on which rule the stylesheet emits last.
    'focus-visible:outline-1 focus-visible:outline-ring focus-visible:outline-solid',
    // Also after `webButtonStyling`, replacing its press shrink: a segmented control selects on
    // click, which fires only when press and release hit the same element, so a segment that shrinks
    // under the pointer loses presses near its edge to the track. iOS Safari re-hit-tests a tap the
    // same way. `touch-manipulation` drops the double-tap zoom wait on touch screens.
    'touch-manipulation [&:not(:disabled)]:active:scale-100'
  ),
  {
    variants: {
      variant: {
        segmented: 'h-[calc(100%-1px)] flex-1 rounded-md border border-transparent px-2 py-1 text-gray-600 opacity-80',
        // The 2px bar takes the place of the header's 1px bottom border under the selected tab: the
        // row is pulled down 1px where it is placed, so bar and border share a line.
        underline:
          'h-11 flex-none rounded-none rounded-t-md border-b-2 border-transparent px-2.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
      },
      selectedState: {
        active: '',
        checked: ''
      },
      /**
       * Paints the selected look whatever the element's state, for an overflow menu trigger that
       * holds the selection: Radix owns `data-state` on a menu trigger and writes open/closed into it.
       */
      selected: {
        true: ''
      }
    },
    compoundVariants: [
      {
        variant: 'segmented',
        selectedState: 'active',
        class:
          'data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:opacity-100 data-[state=active]:shadow-xs'
      },
      {
        variant: 'segmented',
        selectedState: 'checked',
        class:
          'data-[state=checked]:bg-white data-[state=checked]:text-gray-900 data-[state=checked]:opacity-100 data-[state=checked]:shadow-xs'
      },
      {
        variant: 'segmented',
        selected: true,
        class: 'bg-white text-gray-900 opacity-100 shadow-xs'
      },
      {
        variant: 'underline',
        selectedState: 'active',
        class: 'data-[state=active]:border-primary data-[state=active]:text-primary'
      },
      {
        variant: 'underline',
        selected: true,
        class: 'border-primary text-primary'
      }
    ],
    defaultVariants: {
      variant: 'segmented',
      selectedState: 'active'
    }
  }
);
